import { AnalyticsEventType } from "@/constants/analytics";
import { ClusterType } from "@/types";
import { trackEvent } from "@/utils/analytics";

export type ContactSalesClusterLabel =
  | "Bare Metal"
  | "Kubernetes"
  | "Virtual Machine"
  | "Container"
  | "Ray"
  | "Confidential Compute";

export type ContactSalesFormValues = {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  clusterType: ContactSalesClusterLabel;
};

export type ContactSalesFieldErrors = Partial<Record<keyof ContactSalesFormValues, string>>;

export const MANAGED_CLUSTER_TYPE_OPTIONS: { label: ContactSalesClusterLabel; value: ContactSalesClusterLabel }[] = [
  { label: "Bare Metal", value: "Bare Metal" },
  { label: "Confidential Compute", value: "Confidential Compute" },
  { label: "Kubernetes", value: "Kubernetes" },
  { label: "Ray", value: "Ray" }
];

export function clusterTypeToSalesLabel(clusterType: ClusterType | undefined): ContactSalesClusterLabel {
  switch (clusterType) {
    case ClusterType.BARE_METAL:
    case ClusterType.BARE_METAL_REQUEST:
      return "Bare Metal";
    case ClusterType.KUBERNETES:
      return "Kubernetes";
    case ClusterType.VMAAS:
      return "Virtual Machine";
    case ClusterType.CONTAINER:
      return "Container";
    case ClusterType.RAY:
    case ClusterType.RAY_APP:
      return "Ray";
    case ClusterType.CONFIDENTIAL_COMPUTE:
      return "Confidential Compute";
    default:
      return "Bare Metal";
  }
}

export function validateContactSalesForm(values: ContactSalesFormValues): ContactSalesFieldErrors {
  const fieldErrors: ContactSalesFieldErrors = {};
  if (!values.firstName) {
    fieldErrors.firstName = "Required";
  }
  if (!values.lastName) {
    fieldErrors.lastName = "Required";
  }
  if (!values.company) {
    fieldErrors.company = "Required";
  }
  if (!values.email) {
    fieldErrors.email = "Required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    fieldErrors.email = "Invalid email";
  }
  if (!values.clusterType) {
    fieldErrors.clusterType = "Required";
  }
  return fieldErrors;
}

type SalesSubmissionFieldName = "firstname" | "lastname" | "company" | "email" | "cluster_type";

interface SalesSubmissionField {
  name: SalesSubmissionFieldName;
  value: string;
}

interface SalesSubmissionContext {
  pageUri?: string;
  pageName: string;
}

interface SalesSubmission {
  fields: SalesSubmissionField[];
  context: SalesSubmissionContext;
}

export async function submitContactSalesRequest(values: ContactSalesFormValues): Promise<void> {
  trackEvent(AnalyticsEventType.REQUEST_CLUSTER_SUBMIT_CLICKED, {
    clusterType: values.clusterType
  });

  const submissionData: SalesSubmission = {
    fields: [
      { name: "firstname", value: values.firstName },
      { name: "lastname", value: values.lastName },
      { name: "company", value: values.company },
      { name: "email", value: values.email },
      { name: "cluster_type", value: values.clusterType }
    ],
    context: {
      pageUri: typeof window !== "undefined" ? window.location.href : undefined,
      pageName: "Contact our team for managed cluster requests"
    }
  };

  const response = await fetch("/api/request-managed-cluster", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(submissionData)
  });

  if (!response.ok) {
    const msg = await response.text();
    throw new Error(msg || "Submission failed");
  }

  trackEvent(AnalyticsEventType.REQUEST_CLUSTER_SUBMITTED, {
    clusterType: values.clusterType
  });
}
